<template>
  <v-list-item
    class="document-item rounded mb-2"
    :class="{ 'grey darken-4': $vuetify.theme.dark }"
    :key="item.documentId"
    @click="getAsset"
  >
    <v-list-item-content>
      <div class="d-flex flex-column flex-sm-row justify-space-between">
        <div>
          <v-list-item-subtitle class="text-caption text-uppercase">
            <div class="d-flex flex-row align-center">
              <v-icon class="mr-1" large color="gray lighten-1"> {{ mimeIcon(item.mimeType) }} </v-icon>
              {{ mimeFriendly(item.mimeType) }}
            </div>
          </v-list-item-subtitle>
          <v-list-item-title class="pl-1" v-html="item.name"></v-list-item-title>
          <v-list-item-subtitle class="pl-1"> {{ item.description }} </v-list-item-subtitle>
          <v-list-item-subtitle v-if="item.expireOn">
            <span :class="getExpireClass(item.expireOn)" class="pl-1" style="font-size: 0.75rem">
              {{ $t("t_expires") }} {{ formatDateTime(item.expireOn, (standard = false)) }}
            </span>
          </v-list-item-subtitle>
        </div>
        <div class="mt-2 d-flex flex-row align-center">
          <v-btn
            v-if="item.uploadCompletedOn && (item.mimeType?.includes('image') || item.mimeType?.includes('video'))"
            small
            outlined
            color="blue"
            class="mx-1 py-4 rounded"
            @click.stop="previewAsset(item)"
          >
            <v-icon left small>mdi-eye</v-icon> {{ $t("t_preview") }}
          </v-btn>
          <v-btn
            v-if="item.uploadCompletedOn && !(item.mimeType?.includes('url') || item.mimeType == 'application/json')"
            small
            outlined
            color="orange"
            class="mx-1 py-4 rounded"
            @click.stop="downloadAsset(item)"
          >
            <v-icon left small>mdi-download</v-icon> {{ $t("t_download") }}
          </v-btn>
          <v-btn
            v-if="item.uploadCompletedOn"
            small
            outlined
            color="green"
            class="mx-1 py-4 rounded"
            @click.stop="getAsset"
          >
            <v-icon left small>mdi-open-in-new</v-icon> {{ $t("t_open") }}
          </v-btn>
        </div>
      </div>
    </v-list-item-content>
    <file-preview v-model="isPreview" :item="previewFile" @closePreview="isPreview = false"></file-preview>
  </v-list-item>
</template>
<script>
import helpers from "@/mixins/helpers";
import { DocumentService } from "@/services";
import { FilePreview } from "@ranes-inc/shared-lib";
import { saveAs } from "file-saver";

export default {
  name: "DocumentAssetListItem",
  mixins: [helpers],
  props: {
    item: {
      type: Object,
    },
    parent: {
      type: String,
    },
  },
  data() {
    return {
      previewFile: {},
      isPreview: false,
    };
  },
  components: { FilePreview },

  methods: {
    getAsset(document) {
      this.$emit("getAsset", document);
    },
    mimeFriendly(mimeType) {
      if (mimeType.includes("video")) {
        mimeType = "video";
      }
      if (mimeType.includes("image")) {
        mimeType = "image";
      }
      if (mimeType.includes("audio")) {
        mimeType = "audio";
      }
      if (mimeType.includes("url") || mimeType === "application/json") {
        mimeType = "website/url";
      }
      switch (mimeType) {
        case "application/pdf":
          return this.$t("c_documentassetlistitem.t_pdf");
        case "video":
          return this.$t("c_documentassetlistitem.t_video");
        case "image":
          return this.$t("c_documentassetlistitem.t_image");
        case "audio":
          return this.$t("c_documentassetlistitem.t_audio");
        case "website/url":
          return this.$t("c_documentassetlistitem.t_link");
        default:
          return this.$t("c_documentassetlistitem.t_file");
      }
    },
    mimeIcon(mimeType) {
      if (mimeType.includes("video")) {
        mimeType = "video";
      }
      if (mimeType.includes("image")) {
        mimeType = "image";
      }
      if (mimeType.includes("audio")) {
        mimeType = "audio";
      }
      if (mimeType.includes("url") || mimeType === "application/json") {
        mimeType = "website/url";
      }
      switch (mimeType) {
        case "application/pdf":
          return "mdi-file-pdf-box";
        case "video":
          return "mdi-video-box";
        case "image":
          return "mdi-image";
        case "audio":
          return "mdi-volue-high";
        case "website/url":
          return "mdi-link-box-variant";
        default:
          return "mdi-open-in-new";
      }
    },
    getExpireClass(dateStr) {
      const today = new Date();
      const expiry = new Date(dateStr);
      const diffTime = expiry - today;
      const diffDays = diffTime / (1000 * 60 * 60 * 24);

      if (diffDays < 0) {
        return ["red--text", "text--darken-4", "font-weight-bold"];
      } else if (diffDays <= 30) {
        return ["orange--text", "text--darken-2"];
      } else {
        return ["grey--text", "text--darken-1"];
      }
    },
    // Getting the Url from the API to Preview the file
    async previewAsset(asset) {
      const params = {
        documentAssetId: asset.id,
      };
      try {
        this.previewFile = {};
        this.isPreview = true;

        const r = await DocumentService.downloadAsset(params);
        let url;
        // Use thumbnailUrl if available, otherwise use full file url
        if (asset.mimeType.startsWith("image/") && r.data.thumbnailUrl) {
          const imageBlob = await fetch(r.data.thumbnailUrl).then((r) => r.blob());
          const base64 = await this.blobToBase64(imageBlob);
          const base64String = base64.split(",")[1];
          url = `data:image/jpeg;base64,${base64String}`;
        } else {
          url = r.data.downloadUrl;
        }

        this.previewFile = {
          url: url,
          mimeType: asset.mimeType,
          name: asset.originalFileName,
        };
      } catch {
        this.previewFile = { error: "Failed to preview asset!" };
      }
    },

    // Getting the asset URL to Download the asset
    async downloadAsset(asset) {
      const params = {
        documentAssetId: asset.id,
      };

      try {
        const r = await DocumentService.downloadAsset(params);
        const fileUrl = await r.data.downloadUrl;
        const fileExt = asset.originalFileName.substring(asset.originalFileName.lastIndexOf(".") + 1);
        if (asset.mimeType == "application/json" && fileExt !== "json") {
          fetch(fileUrl)
            .then((res) => res.json())
            .then((data) => {
              const url = data.url;
              const a = document.createElement("a");
              a.setAttribute("href", url);
              window.open(url, "_blank");
            });
        } else {
          fetch(fileUrl)
            .then((res) => res.blob())
            .then((blob) => {
              saveAs(blob, asset.name);
            });
        }
      } catch {
        console.error("Failed to download asset!");
      }
    },

    // Converting blob to base64
    blobToBase64(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = function () {
          const base64data = reader.result;
          resolve(base64data);
        };
        reader.onerror = function (error) {
          reject(error);
        };
      });
    },
  },
};
</script>
<style scoped>
.text-wrap {
  white-space: normal !important;
  word-break: break-word;
  overflow: visible !important;
}
</style>
